/**
 * MBCB reports store `reportMonth` / `reportYear` as the **ministry month** being reported
 * (e.g. March). The report is commonly **named** by the month it is **submitted**—usually the
 * following calendar month (e.g. "April report").
 */

export function formatCalendarMonthYear(month: number, year: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, 1),
  );
}

/** The month/year when this report is typically filed (calendar month after the ministry month). */
export function getFilingMonthYearAfterMinistry(ministryMonth: number, ministryYear: number) {
  if (ministryMonth === 12) return { month: 1, year: ministryYear + 1 };
  return { month: ministryMonth + 1, year: ministryYear };
}

export function formatFilingMonthLabel(ministryMonth: number, ministryYear: number): string {
  const { month, year } = getFilingMonthYearAfterMinistry(ministryMonth, ministryYear);
  return formatCalendarMonthYear(month, year);
}

/** Human-readable ministry period (matches DB `reportMonth` / `reportYear`). */
export function formatMinistryMonthLabel(ministryMonth: number, ministryYear: number): string {
  return formatCalendarMonthYear(ministryMonth, ministryYear);
}

export function mbcbReportNamedTitle(ministryMonth: number, ministryYear: number): string {
  return `${formatFilingMonthLabel(ministryMonth, ministryYear)} report`;
}

export function mbcbMinistryPeriodCaption(ministryMonth: number, ministryYear: number): string {
  return `Covers ${formatMinistryMonthLabel(ministryMonth, ministryYear)} ministry`;
}

/** Example labels based on today (filing month = this calendar month; ministry = previous month). */
export function mbcbNamingExampleFromToday(): { filingLabel: string; ministryLabel: string } {
  const t = new Date();
  const ty = t.getFullYear();
  const tm = t.getMonth();
  const ministryMonth = tm === 0 ? 12 : tm;
  const ministryYear = tm === 0 ? ty - 1 : ty;
  const filingMonth = tm + 1;
  return {
    filingLabel: formatCalendarMonthYear(filingMonth, ty),
    ministryLabel: formatCalendarMonthYear(ministryMonth, ministryYear),
  };
}
